import moment from "moment";
import { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CurrentLocation from "src/components/currentLocation";
import Button from "src/components/custom/Button";
import Modal from "src/components/modal";
import QuestionnaireModal from "src/components/modal/QuestionnaireModal";
import {
  postMethodApiCall,
  getMethodApiCall,
  putMethodApiCall,
} from "src/services/api";
import { useTranslation } from "react-i18next";
import { BATCH_YEARS, GENDER_OPTIONS } from "src/utils/constants";
import CustomInput from "src/components/custom/CustomInput";
import CustomSelect from "src/components/custom/CustomSelect";
import CertificateButton from "src/components/certificate/Certificatebutton";

const TraineeDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [load, setLoad] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // New state for button processing

  const timerIntervalRef = useRef(null);
  const isCheckingOutRef = useRef(false);
  const warningShownRef = useRef(false);
  const [details, setDetails] = useState(
    JSON.parse(localStorage.getItem("loginedUser")),
  );
  const currentUser = JSON.parse(localStorage.getItem("loginedUser"));
  const registrationId =
    currentUser?.trainee?.registration_id || currentUser?.registration_id;

  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [isLoadingAttendance, setIsLoadingAttendance] = useState(true);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [verifiedImageUrl, setVerifiedImageUrl] = useState("");
  const [isVerifyingPhoto, setIsVerifyingPhoto] = useState(false);
  const [hasFaceReference, setHasFaceReference] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [showSelfieModal, setShowSelfieModal] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [isCameraStarting, setIsCameraStarting] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraStreamRef = useRef(null);

  // Dropdown Options State
  const [districts, setDistricts] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [itis, setItis] = useState([]);
  const [trades, setTrades] = useState([]);

  const [isDisabled, setIsDisabled] = useState({
    iti: true,
    trade: true,
    industry: true,
  });

  const [timer, setTimer] = useState({
    hours: "00",
    minutes: "00",
    seconds: "00",
  });
  const [, setIsCheckingOut] = useState(false);
  const [isProfileSavedInDB, setIsProfileSavedInDB] = useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [showImageError, setShowImageError] = useState(false);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [pendingAttendance, setPendingAttendance] = useState(null);
  const [checkoutAfterQuestionnaire, setCheckoutAfterQuestionnaire] =
    useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: { ...details },
  });

  // Check if all required fields are filled with actual data (not placeholders)
  useEffect(() => {
    const requiredDbFields = [
      "registration_id",
      "name",
      "phone_number",
      "gender",
      "date_of_birth",
      "father_name",
      "mother_name",
      "batch_of_trade",
      "district_of_ITI",
      "iti_id",
      "trade_id",
      "industry_id",
      "isInformationCorrect",
    ];

    // Check if details state has valid values for all these fields
    const isSaved = requiredDbFields.every((field) => {
      const val = details?.[field];
      return val && val !== "" && val !== null && val !== undefined;
    });

    setIsProfileSavedInDB(isSaved);
  }, [details]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/Trainee-login");
      return;
    }

    const fetchUserData = async () => {
      setIsLoadingAttendance(true);
      try {
        // Fetch user data from the server using registration ID
        const response = await getMethodApiCall(
          `/v1/api/trainee/${registrationId}`,
        );
        const userData = response.data.trainee;

        // 1. Update details state immediately
        setDetails(userData);
        setHasFaceReference(Boolean(userData?.face_image_url));

        const attendance =
          userData?.attendances?.length > 0 ? userData.attendances[0] : null;
        setTodayAttendance(attendance);

        // Check for pending questionnaire from a previous attendance
        const traineeId = userData?.id;
        if (traineeId) {
          const pendingRes = await getMethodApiCall(
            `/v1/api/trainee/questionnaire/pending/${traineeId}`,
          );
          if (pendingRes?.data?.success && pendingRes.data.data) {
            setPendingAttendance(pendingRes.data.data);
            setShowQuestionnaire(true);
          }
        }

        const districtId =
          attendance?.district || userData?.district_of_ITI || "";
        const itiId = attendance?.iti_id || userData?.iti_id || "";
        const tradeId = attendance?.trade_id || userData?.trade_id || "";
        const industryId =
          attendance?.industry_id || userData?.industry_id || "";
        const batch =
          attendance?.batch_of_trade || userData?.batch_of_trade || "";

        const districtRes = await getMethodApiCall(
          `/v1/api/trainee/get_district_picklist`,
        );
        const districtList = districtRes?.data?.data || [];
        setDistricts(districtList);

        if (districtId) {
          const itiList = await fetchItisList(districtId);
          setItis(itiList);
          setSelectedDistrict(districtId);
          setIsDisabled((prev) => ({ ...prev, iti: false }));
        }

        if (itiId) {
          const tradeRes = await fetchTradesByITI(itiId);
          setTrades(tradeRes?.trades || []);
          setIsDisabled((prev) => ({ ...prev, trade: false }));
        }

        if (tradeId && itiId) {
          const industryRes = await fetchIndustriesByTradeAndITI(
            tradeId,
            itiId,
          );
          const industryList = industryRes?.industries || [];
          setIndustries(industryList);
          setIsDisabled((prev) => ({ ...prev, industry: false }));

          const selectedIndustryObj = industryList.find(
            (ind) => ind.id === industryId,
          );
          if (selectedIndustryObj) {
            setSelectedIndustry(selectedIndustryObj);
          }
        }

        // 4. Reset Form with User Profile Data
        reset({
          ...userData,
          date_of_birth: userData?.date_of_birth
            ? moment(userData.date_of_birth).format("YYYY-MM-DD")
            : "",
          district_of_ITI: districtId,
          iti_id: itiId,
          trade_id: tradeId,
          industry_id: industryId,
          batch_of_trade: batch,
          isInformationCorrect: userData?.isInformationCorrect === "Yes",
          gender: userData?.gender || "",
        });
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setIsLoadingAttendance(false);
      }
    };
    fetchUserData();
  }, [navigate, registrationId, reset, setValue]);

  // --- HELPER FETCHERS (Only used if NO attendance data) ---
  const fetchItisList = async (district) => {
    if (!district) return [];
    try {
      const res = await getMethodApiCall(
        `/v1/api/trainee/get_iti_picklist/${district}`,
      );
      return res?.data?.data || [];
    } catch {
      return [];
    }
  };

  const fetchTradesByITI = async (iti_name) => {
    if (!iti_name) return { trades: [] };
    try {
      const res = await getMethodApiCall(
        `/v1/api/trainee/trades_by_iti/${iti_name}`,
      );
      return res?.data?.success
        ? { trades: res.data.data || [] }
        : { trades: [] };
    } catch (error) {
      console.error("Error fetching trades:", error);
      return { trades: [] };
    }
  };

  const fetchIndustriesByTradeAndITI = async (tradeId, itiId) => {
    const resolvedItiId = itiId || watch("iti_id") || details?.trainee?.iti_id;
    const resolvedTradeId =
      tradeId || watch("trade_id") || details?.trainee?.trade_id;

    if (!resolvedTradeId || !resolvedItiId) return { industries: [] };
    try {
      const res = await getMethodApiCall(
        `/v1/api/trainee/industries_by_trade_and_iti/${resolvedTradeId}/${resolvedItiId}`,
      );
      return res?.data?.success
        ? { industries: res.data.data || [] }
        : { industries: [] };
    } catch (error) {
      console.error("Error fetching industries:", error);
      return { industries: [] };
    }
  };

  // --- KEEP EXISTING HANDLERS FOR MANUAL SELECTION (Only works in Case B) ---
  const handleDistrictChange = async (district) => {
    setSelectedDistrict(district);
    const list = await fetchItisList(district);
    setItis(list);
    setIsDisabled((prev) => ({
      ...prev,
      iti: false,
      trade: true,
      industry: true,
    }));
    setValue("iti_id", "");
    setValue("trade_id", "");
    setValue("industry_id", "");
  };

  const handleItiChange = async (iti_id) => {
    setValue("iti_id", iti_id);
    const result = await fetchTradesByITI(iti_id);
    setTrades(result.trades || []);
    setValue("trade_id", "");
    setValue("industry_id", "");
    setIsDisabled((prev) => ({ ...prev, trade: false, industry: true }));
  };

  const handleTradeChange = async (trade_id) => {
    setValue("trade_id", trade_id);
    const currentItiId = watch("iti_id");
    const result = await fetchIndustriesByTradeAndITI(trade_id, currentItiId);
    setIndustries(result.industries);
    setValue("industry_id", "");
    setIsDisabled((prev) => ({ ...prev, industry: false }));
  };

  const clearSelectedImage = () => {
    setSelectedImageFile(null);
    setVerifiedImageUrl("");
    setImagePreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
  };

  const setAttendanceImageFile = (file) => {
    setSelectedImageFile(file);
    setVerifiedImageUrl("");
    setImagePreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setShowImageError(false);
  };

  const stopCameraStream = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const closeSelfieModal = () => {
    stopCameraStream();
    setShowSelfieModal(false);
    setCameraError("");
    setIsCameraStarting(false);
  };

  const openSelfieCamera = () => {
    setCameraError("");
    setIsCameraStarting(true);
    setShowSelfieModal(true);
  };

  useEffect(() => {
    if (!showSelfieModal) return undefined;

    let cancelled = false;

    const startCamera = async () => {
      try {
        if (!window.isSecureContext) {
          throw new Error(
            "Camera needs a secure page. Open the app as http://localhost:3000 (not a network IP)."
          );
        }

        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("Camera is not supported in this browser.");
        }

        // Do NOT use enumerateDevices before permission — browsers often hide cameras until allowed.
        const attempts = [
          { video: { facingMode: "user" }, audio: false },
          { video: { facingMode: "environment" }, audio: false },
          { video: true, audio: false },
        ];

        let stream = null;
        let lastError = null;

        for (const constraints of attempts) {
          try {
            stream = await navigator.mediaDevices.getUserMedia(constraints);
            break;
          } catch (err) {
            lastError = err;
          }
        }

        if (!stream) {
          throw lastError || new Error("Unable to access camera.");
        }

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        cameraStreamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (error) {
        console.error("Unable to start camera:", error);
        if (!cancelled) {
          const name = error?.name || "";
          const message = error?.message || "";
          if (name === "NotFoundError" || message.includes("Requested device not found")) {
            setCameraError(
              "Browser cannot access the webcam. 1) Open Windows Camera app to confirm it works 2) Settings → Privacy → Camera → allow desktop apps 3) Close Zoom/Teams 4) In browser address bar allow Camera for this site."
            );
          } else if (name === "NotAllowedError" || name === "PermissionDeniedError") {
            setCameraError(
              "Camera permission blocked. Click the lock/camera icon in the address bar → allow Camera → refresh and try again."
            );
          } else if (name === "NotReadableError" || name === "TrackStartError") {
            setCameraError(
              "Camera is busy. Close Zoom/Teams/Camera app, then try Take Selfie again."
            );
          } else {
            setCameraError(
              message ||
                "Unable to access camera. Please allow camera permission and try again."
            );
          }
        }
      } finally {
        if (!cancelled) setIsCameraStarting(false);
      }
    };

    startCamera();

    return () => {
      cancelled = true;
      stopCameraStream();
    };
  }, [showSelfieModal]);

  const captureSelfie = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) {
      setCameraError("Camera is not ready yet. Please wait a moment.");
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setCameraError("Failed to capture selfie. Please try again.");
          return;
        }
        const file = new File([blob], `selfie-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        setAttendanceImageFile(file);
        closeSelfieModal();
      },
      "image/jpeg",
      0.92
    );
  };

  useEffect(() => {
    return () => {
      stopCameraStream();
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reportDetails = async () => {
    setLoad(true);
    window.open("https://forms.office.com/r/Mc5s4vxzPT", "_blank");
    setLoad(false);
  };

  // --- UPDATE PROFILE (The "Update & Continue" Button) ---
  const handleUpdateProfile = async () => {
    setIsUpdatingProfile(true);
    setShowImageError(false);
    try {
      const formData = new FormData();
      const currentValues = watch();
      const fieldsToUpdate = [
        "name",
        "phone_number",
        "date_of_birth",
        "father_name",
        "mother_name",
        "gender",
        "batch_of_trade",
        "district_of_ITI",
        "city_of_ITI",
        "latitude",
        "longitude",
        "iti_id",
        "trade_id",
        "industry_id",
        "isInformationCorrect",
        "course_affiliation_",
      ];

      fieldsToUpdate.forEach((field) => {
        if (field === "isInformationCorrect") {
          formData.append(field, currentValues[field] === true ? "Yes" : "No");
        } else if (currentValues[field]) {
          formData.append(field, String(currentValues[field]));
        }
      });
      formData.append("registration_id", String(registrationId));

      const response = await putMethodApiCall(
        "/v1/api/trainee/update-profile",
        formData,
      );
      const result = await response.data;

      if (response.status === 200 && result.success) {
        toast.success("Profile updated successfully!");

        // 1. Update LocalStorage
        const updatedUser = { ...currentUser, trainee: result.data };
        localStorage.setItem("loginedUser", JSON.stringify(updatedUser));

        // 2. Update Local State (Details) -> This flips 'isProfileSavedInDB' to true
        setDetails(result.data);

        // 3. Reset form to ensure sync
        reset({
          ...result.data,
          date_of_birth: result.data?.date_of_birth
            ? moment(result.data.date_of_birth).format("YYYY-MM-DD")
            : "",
        });
      }
    } catch (error) {
      console.error("Failed to update profile", error);
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  function calculateDistance(lat1, lon1, lat2, lon2) {
    // Convert to numbers (in case any value is string)
    lat1 = parseFloat(lat1);
    lon1 = parseFloat(lon1);
    lat2 = parseFloat(lat2);
    lon2 = parseFloat(lon2);

    const R = 6371; // Radius of the Earth in km
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * (Math.PI / 180)) *
        Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) ** 2;

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c <= 1; //  within 1 km
  }

  // Modified attendance/checkout logic
  // Unified handler: decides between record attendance or checkout based on current attendance state
  const handleAttendanceOrCheckout = async (formValues) => {
    if (todayAttendance && !todayAttendance.checkout_time) {
      await handleCheckout();
      return;
    }

    // Check if image is uploaded for attendance
    if (!selectedImageFile) {
      setShowImageError(true);
      return;
    }

    if (!verifiedImageUrl) {
      toast.error("Please verify the photo before recording attendance.");
      return;
    }

    setShowImageError(false);
    onSubmit(formValues);
  };

  const saveAttendancePhoto = async () => {
    if (!selectedImageFile || isVerifyingPhoto) return "";

    setIsVerifyingPhoto(true);
    try {
      const imageFormData = new FormData();
      imageFormData.append("image_url", selectedImageFile);
      imageFormData.append(
        "trainee_id",
        details?.id ||
          details?.trainee_id ||
          currentUser?.trainee?.id ||
          currentUser?.id,
      );

      const imageRes = await postMethodApiCall(
        "/v1/api/trainee/upload-attendance-image",
        imageFormData,
      );

      if (!(imageRes?.status === 200 && imageRes?.data?.success)) {
        setVerifiedImageUrl("");
        toast.error(
          imageRes?.data?.message ||
            "Face verification failed. Please upload a clear photo and try again.",
        );
        return "";
      }

      setVerifiedImageUrl("verified");
      return "verified";
    } catch (error) {
      console.error("Photo verification failed:", error);
      setVerifiedImageUrl("");
      toast.error("Face verification failed. Please try again.");
      return "";
    } finally {
      setIsVerifyingPhoto(false);
    }
  };

  const verifyAttendancePhoto = async () => {
    const imageUrl = await saveAttendancePhoto();
    if (!imageUrl) return;
    toast.success("Photo verified. You can now record attendance.");
  };

  // Record Attendance: photo is already verified; create attendance row after location check
  const recordTraineeAttendance = async (locationData) => {
    const formData = new FormData();
    if (selectedImageFile) {
      formData.append("registration_id", details?.registration_id);
      formData.append("name", details?.name);
      formData.append("batch_of_trade", details?.batch_of_trade);
      formData.append("latitude", locationData?.latitude || details?.latitude);
      formData.append("status", "Pending");
      formData.append("iti_id", details?.iti_id);
      formData.append("trade_id", details?.trade_id);
      formData.append("industry_id", details?.industry_id);
      formData.append(
        "longitude",
        locationData?.longitude || details?.longitude,
      );
      formData.append("district", selectedDistrict);
    }

    // List of industry names that should bypass the coordinate check
    const bypassIndustries = [
      "Azad Industry",
      "Dashmesh Tractors",
      "Harnam Singh & Sons",
      "Jaswant Singh & Sons",
      "Kabir Engg. Works",
      "The Shahbad Co-Op Sugar Mills Ltd.",
      "Aman Vishavkarma Engg. Works",
      "Jindal Lifestyle Ltd., Rohad",
      "OM Diesels Pvt Ltd.",
      "Public Health Engineering Deptt.",
      "BJD Construction Company",
      "Vipin Motors Pvt. Ltd.",
      "C-279, Nirankari Marg, Block E, JJ Colony Wazirpur, Ashok Vihar, Delhi, 110052",
    ];

    // Check if the selected industry is in the bypass list
    const shouldBypassCheck = bypassIndustries.includes(
      details?.name_of_industry_partner,
    );

    // Use location from CurrentLocation component if available, otherwise use details
    const traineeLat = locationData?.latitude || details?.latitude;
    const traineeLon = locationData?.longitude || details?.longitude;

    if (!verifiedImageUrl) {
      toast.error("Please verify the photo before recording attendance.");
      return;
    }

    if (
      shouldBypassCheck ||
      calculateDistance(
        traineeLat,
        traineeLon,
        selectedIndustry?.latitude,
        selectedIndustry?.longitude,
      )
    ) {
      formData.append("image_url", selectedImageFile);

      const res = await postMethodApiCall(
        "/v1/api/trainee/attendance/post",
        formData,
      );

      if (res?.status === 201 && res?.data?.success) {
        setOpenModal(false);
        const attendanceData = res.data.data || {};
        toast.success("Attendance Marked Successfully");
        setTodayAttendance(attendanceData);
        setHasFaceReference(true);
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        const startTs = new Date(
          attendanceData.checkin_time || attendanceData.date,
        ).getTime();
        warningShownRef.current = false;
        timerIntervalRef.current = startDynamicTimer(
          startTs,
          setTimer,
          handleCheckout,
        );
        clearSelectedImage();
      } else {
        toast.error(
          res?.data?.message || "Failed to mark attendance. Please try again."
        );
      }
    }
  };



  // Show questionnaire before checkout; after submit, proceed with actual checkout
  const handleCheckoutWithQuestionnaire = () => {
    setPendingAttendance(todayAttendance);
    setCheckoutAfterQuestionnaire(true);
    setShowQuestionnaire(true);
  };

  // Checkout handler - sends checkout_time for latest attendance
  const handleCheckout = async () => {
    if (!todayAttendance) return;

    // Set flag to prevent timer restart during checkout
    isCheckingOutRef.current = true;
    setIsCheckingOut(true);
    setIsProcessing(true); // Start processing
    try {
      const res = await postMethodApiCall("/v1/api/logout-time", {
        trainee_id: todayAttendance.trainee_id,
        attendance_id: todayAttendance.id,
      });
      if (res?.status === 200 && res?.data?.success) {
        toast.success("Checked out successfully.");
        // IMMEDIATELY stop the timer interval before any state updates
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }

        // Set timer display to zero immediately
        setTimer({ hours: "00", minutes: "00", seconds: "00" });
        // Finally, update attendance state with checkout_time

        setTodayAttendance(res.data.data);
      }
    } catch (err) {
      console.error("Checkout error:", err);
    } finally {
      isCheckingOutRef.current = false;
      setIsCheckingOut(false);
      setIsProcessing(false); // Stop processing
    }
  };

  const onSubmit = async (data) => {
    if ("geolocation" in navigator) {
      setOpenModal(true);
      navigator.geolocation.getCurrentPosition(function (position) {
        setDetails({
          ...data,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      });
    } else {
      console.warn("Geolocation is not available in your browser.");
      setIsProcessing(false); // Stop processing if geolocation fails
    }
  };

  function startDynamicTimer(startTime, setTimer, onAutoCheckout) {
    return setInterval(() => {
      const now = Date.now();
      const elapsedSec = Math.floor((now - startTime) / 1000);
      const SIXTEEN_HOURS_SEC = 16 * 60 * 60;
      if (elapsedSec >= SIXTEEN_HOURS_SEC) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
        console.error("Auto-checkout triggered after 16 hours.");
        onAutoCheckout(); // Call handleCheckout() automatically
        return;
      }
      setTimer({
        hours: String(Math.floor(elapsedSec / 3600)).padStart(2, "0"),
        minutes: String(Math.floor((elapsedSec % 3600) / 60)).padStart(2, "0"),
        seconds: String(elapsedSec % 60).padStart(2, "0"),
      });
    }, 1000);
  }

  useEffect(() => {
    // CRITICAL: If checkout is in progress, don't do anything
    if (isCheckingOutRef.current) return;
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    // Check if checkout has happened - if so, don't start timer
    if (todayAttendance && todayAttendance.checkout_time) {
      // hours_logged is now a string in hh:mm:ss format
      let loggedTime = { hours: "00", minutes: "00", seconds: "00" };
      if (
        typeof todayAttendance.hours_logged === "string" &&
        todayAttendance.hours_logged.includes(":")
      ) {
        const [h, m, s] = todayAttendance.hours_logged.split(":");
        loggedTime = {
          hours: h?.padStart(2, "0") || "00",
          minutes: m?.padStart(2, "0") || "00",
          seconds: s?.padStart(2, "0") || "00",
        };
      }
      setTimer(loggedTime);
      warningShownRef.current = false;
      return;
    }

    if (
      todayAttendance &&
      !todayAttendance.checkout_time &&
      todayAttendance.checkin_time
    ) {
      warningShownRef.current = false;
      const startTime = new Date(todayAttendance.checkin_time).getTime();
      // Pass handleCheckout as the auto-checkout callback
      timerIntervalRef.current = startDynamicTimer(
        startTime,
        setTimer,
        handleCheckout,
      );
    } else if (!todayAttendance) {
      setTimer({ hours: "00", minutes: "00", seconds: "00" });
    }
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [todayAttendance]);

  const canRecordAttendance =
    isProfileSavedInDB &&
    Boolean(selectedImageFile) &&
    Boolean(verifiedImageUrl);

  // Function to handle button click
  const handleAttendanceButtonClick = async () => {
    if (isProcessing || isLoadingAttendance) return; // Prevent multiple clicks

    if (todayAttendance && !todayAttendance.checkout_time) {
      // Show questionnaire before checkout
      handleCheckoutWithQuestionnaire();
    } else {
      // Handle check-in
      await handleSubmit(handleAttendanceOrCheckout)();
    }
  };

  console.log(pendingAttendance, "pendingAttendance");

  return (
    <div className="w-full">
      <div className="relative h-full flex justify-center items-center py-3 container mx-auto">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full px-8 py-6 mt-4 text-left bg-white rounded-md shadow-sm"
        >
          <div className="flex items-center justify-between gap-2">
            <p className="m-0 text-[16px] font-semibold text-primary">
              {t("traineeDetails.title")}
            </p>
            <div className="gap-2 flex flex-col items-center justify-center md:flex-row">
              <div className="flex items-center gap-2 p-2 rounded-2xl border border-teal-200 bg-white w-fit shadow-sm">
                <div className="bg-teal-700 text-white px-2 py-1 rounded-xl text-xl font-semibold">
                  {timer.hours}
                </div>
                <span className="text-teal-700 text-2xl font-bold">:</span>
                <div className="bg-teal-700 text-white px-2 py-1 rounded-xl text-xl font-semibold">
                  {timer.minutes}
                </div>
                <span className="text-teal-700 text-2xl font-bold">:</span>
                <div className="bg-teal-700 text-white px-2 py-1 rounded-xl text-xl font-semibold">
                  {timer.seconds}
                </div>
              </div>
              <Button
                title={t("traineeDetails.attendanceHistory")}
                className="py-2 px-8 !w-32 text-xs text-primary"
                onClick={() => navigate("/attendance-history")}
              />

              <CertificateButton registrationId={registrationId} />
              {/* <Button
                disabled={load}
                onClick={reportDetails}
                title={
                  load
                    ? t("traineeDetails.processing")
                    : t("traineeDetails.report")
                }
                className="py-2 px-8 !w-32 text-xs text-primary"
                type="button"
              /> */}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 mt-4 lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-1">
            {/* 1. Registration ID - Always Disabled */}
            <CustomInput
              label={<>Enter your registration number </>}
              name="registration_id"
              register={register}
              validation={{ required: t("validation.registrationIdRequired") }}
              error={errors.registration_id}
              disabled={true}
              placeholder={t("traineeDetails.registrationNumber")}
            />

            {/* 2. Full Name - Disabled if Saved */}
            <CustomInput
              label={<>Full Name</>}
              name="name"
              register={register}
              validation={{ required: t("validation.fullNameRequired") }}
              error={errors.name}
              disabled={isProfileSavedInDB}
              placeholder={t("traineeDetails.fullName")}
            />

            {/* 3. Phone - Disabled if Saved */}
            <CustomInput
              label={<>Phone Number</>}
              name="phone_number"
              type="tel"
              register={register}
              validation={{
                required: t("validation.phoneNumberRequired"),
                pattern: {
                  value: /^[0-9]{10}$/,
                  message: t("validation.phoneNumberInvalid"),
                },
              }}
              error={errors.phone_number}
              disabled={isProfileSavedInDB}
              placeholder={t("traineeDetails.phoneNumber")}
            />

            {/* 4. Gender - Disabled if Saved */}
            <CustomSelect
              label="Select Gender"
              name="gender"
              register={register}
              validation={{ required: t("validation.genderRequired") }}
              options={GENDER_OPTIONS}
              error={errors.gender}
              disabled={isProfileSavedInDB}
              placeholder={t("traineeDetails.gender")}
            />

            {/* 5. DOB - Disabled if Saved */}
            <CustomInput
              label={<>Date of Birth</>}
              name="date_of_birth"
              type="date"
              register={register}
              validation={{ required: t("validation.dateOfBirthRequired") }}
              error={errors.date_of_birth}
              disabled={isProfileSavedInDB}
              max={
                new Date(
                  new Date().getFullYear() - 14,
                  new Date().getMonth(),
                  new Date().getDate(),
                )
                  .toISOString()
                  .split("T")[0]
              }
            />

            {/* 6. Father */}
            <CustomInput
              label={<>Father’s Name</>}
              name="father_name"
              register={register}
              validation={{ required: t("validation.fatherNameRequired") }}
              error={errors.father_name}
              disabled={isProfileSavedInDB}
              placeholder={t("traineeDetails.fatherName")}
            />

            {/* 7. Mother - Disabled if Saved */}
            <CustomInput
              label={<>Mother’s Name</>}
              name="mother_name"
              register={register}
              validation={{ required: t("validation.motherNameRequired") }}
              error={errors.mother_name}
              disabled={isProfileSavedInDB}
              placeholder={t("traineeDetails.motherName")}
            />

            {/* 8. Batch - Disabled if Saved */}
            <CustomSelect
              label="Select Batch"
              name="batch_of_trade"
              register={register}
              validation={{ required: t("validation.batchRequired") }}
              options={BATCH_YEARS}
              error={errors.batch_of_trade}
              disabled={isProfileSavedInDB}
              placeholder={t("traineeDetails.selectBatch")}
            />

            {/* 9. District */}
            <CustomSelect
              label="Select District"
              name="district_of_ITI"
              register={register}
              validation={{ required: t("validation.districtRequired") }}
              options={districts}
              valueKey="district"
              labelKey="district"
              error={errors.district_of_ITI}
              placeholder={t("traineeDetails.selectDistrict")}
              onChange={(e) => handleDistrictChange(e.target.value)}
            />

            {/* 10. ITI */}
            <CustomSelect
              label="Select ITI"
              name="iti_id"
              register={register}
              validation={{ required: t("validation.itiNameRequired") }}
              options={itis}
              valueKey="id"
              labelKey="iti_name"
              error={errors.iti_id}
              disabled={isDisabled.iti}
              placeholder={t("traineeDetails.selectITI")}
              onChange={(e) => handleItiChange(e.target.value)}
            />

            {/* 11. Trade */}
            <CustomSelect
              label="Select Trade"
              name="trade_id"
              register={register}
              validation={{ required: t("validation.tradeRequired") }}
              options={trades}
              valueKey="id"
              labelKey="trade_name"
              error={errors.trade_id}
              disabled={isDisabled.trade}
              placeholder={t("traineeDetails.selectTrade")}
              onChange={(e) => handleTradeChange(e.target.value)}
            />

            {/* 12. Industry */}
            <CustomSelect
              label="Select Industry"
              name="industry_id"
              register={register}
              validation={{ required: t("validation.industryRequired") }}
              options={industries}
              valueKey="id"
              labelKey="industry_name"
              error={errors.name_of_industry_partner}
              disabled={isDisabled.industry}
              placeholder={t("traineeDetails.selectIndustry")}
              onChange={(e) => {
                const ind = industries.find(
                  (i) => i.id === parseInt(e.target.value),
                );
                if (ind) {
                  setSelectedIndustry(ind);
                  setValue("latitude", ind.latitude);
                  setValue("longitude", ind.longitude);
                }
              }}
            />

            {/* 14. Course Affiliation - Disabled if Saved */}
            <div className="w-full flex flex-col gap-2">
              <label className="font-[400] text-xs text-gray-600">
                Course Affiliation
              </label>
              <div className="flex mt-1.5 text-gray-500 gap-2 custom-radio">
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    value="NCVT"
                    disabled={isProfileSavedInDB}
                    {...register("course_affiliation_")}
                  />{" "}
                  NCVT
                </label>
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    value="SCVT"
                    disabled={isProfileSavedInDB}
                    {...register("course_affiliation_")}
                  />{" "}
                  SCVT
                </label>
              </div>
            </div>

            {/* 15. Is this information correct? - Hidden if profile is saved */}
            {!isProfileSavedInDB && (
              <div className="w-full flex flex-col gap-2">
                <div className="flex items-center gap-2 mt-6">
                  <input
                    type="checkbox"
                    id="isInformationCorrect"
                    {...register("isInformationCorrect", {
                      required: t("traineeDetails.isInformationCorrect"),
                    })}
                    className="w-5 h-5 accent-teal-700 text-teal-700 bg-gray-100 border-gray-300 rounded focus:ring-teal-700 focus:ring-2 cursor-pointer"
                  />
                  <label
                    htmlFor="isInformationCorrect"
                    className="font-[400] text-sm text-gray-600 cursor-pointer select-none"
                  >
                    Is this information correct?{" "}
                    <span className="text-red-400">*</span>
                  </label>
                </div>
                {errors.isInformationCorrect && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors.isInformationCorrect.message}
                  </span>
                )}
              </div>
            )}

            {/* 16. Image Upload (Moved to end of form) - VISIBLE only when Profile is COMPLETE and Attendance is PENDING */}
            {isProfileSavedInDB && (
              <div className="w-full flex flex-col gap-2">
                <label className="font-[400] text-xs text-gray-600">
                  Take Photo at Industry{" "}
                  <span className="text-red-400">*</span>
                </label>
                {!todayAttendance && !isLoadingAttendance && !hasFaceReference && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    This first photo is your daily reference. Later photos are
                    checked against it, so keep your face clear.
                  </p>
                )}
                <div className="flex">
                  <Button
                    type="button"
                    className="w-full sm:w-auto px-[10px] font-normal shrink-0"
                    title="Take Selfie"
                    onClick={openSelfieCamera}
                  />
                </div>
                {imagePreviewUrl && (
                  <div className="mt-2 flex items-start gap-3">
                    <img
                      src={imagePreviewUrl}
                      alt="Selected attendance"
                      className="h-28 w-28 object-cover rounded border border-gray-200"
                    />
                    <div className="flex flex-col items-start gap-2">
                      <button
                        type="button"
                        className="text-xs text-red-500 underline mt-1"
                        onClick={clearSelectedImage}
                        disabled={isVerifyingPhoto}
                      >
                        Remove photo
                      </button>
                      {!todayAttendance &&
                        !isLoadingAttendance &&
                        (verifiedImageUrl ? (
                          <span className="text-xs text-green-600">
                            Photo verified. Click Record Attendance to continue.
                          </span>
                        ) : (
                          <Button
                            type="button"
                            className="px-[10px] font-normal"
                            title={
                              isVerifyingPhoto ? (
                                <div className="flex items-center gap-2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  Verifying Photo...
                                </div>
                              ) : (
                                "Verify Photo"
                              )
                            }
                            disabled={isVerifyingPhoto}
                            onClick={verifyAttendancePhoto}
                          />
                        ))}
                    </div>
                  </div>
                )}
                {showImageError && !selectedImageFile && (
                  <span className="text-red-500 text-xs">
                    {t("validation.imageRequired")}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* ACTION BUTTONS */}
          <div className="w-full mt-6 flex justify-center items-center space-x-2">
            {todayAttendance && !todayAttendance.checkout_time ? (
              // CASE 1: Checked In -> Show Check-Out
              <Button
                className="w-auto px-[10px] font-normal"
                onClick={handleCheckoutWithQuestionnaire}
                title="Check-Out"
                type="button"
              />
            ) : todayAttendance && todayAttendance.checkout_time ? (
              // CASE 2: Day Complete
              <p className="text-green-600 font-medium">
                Attendance recorded successfully for the day.
              </p>
            ) : canRecordAttendance ? (
              <Button
                className="w-auto px-[10px] font-normal"
                disabled={isLoadingAttendance || isProcessing || isVerifyingPhoto}
                onClick={handleAttendanceButtonClick}
                title={
                  isProcessing ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {todayAttendance
                        ? "Checking Out..."
                        : "Recording Attendance..."}
                    </div>
                  ) : isLoadingAttendance ? (
                    "Loading..."
                  ) : todayAttendance ? (
                    "Check-Out"
                  ) : (
                    t("traineeDetails.recordAttendance")
                  )
                }
                type="button"
              />
            ) : !isProfileSavedInDB ? (
              // Profile not saved yet
              <Button
                className={`w-auto px-[10px] font-normal ${
                  isUpdatingProfile
                    ? "cursor-not-allowed opacity-60"
                    : "cursor-pointer"
                }`}
                title={isUpdatingProfile ? "Updating..." : "Update & Continue"}
                onClick={handleSubmit(handleUpdateProfile)}
                disabled={isUpdatingProfile}
                type="button"
              />
            ) : null}
          </div>
        </form>
      </div>
      <QuestionnaireModal
        isOpen={showQuestionnaire}
        attendanceId={pendingAttendance?.id}
        date={pendingAttendance?.date}
        traineeId={details?.id}
        onComplete={async () => {
          setShowQuestionnaire(false);
          setPendingAttendance(null);
          if (checkoutAfterQuestionnaire) {
            setCheckoutAfterQuestionnaire(false);
            await handleCheckout();
          }
        }}
        onClose={() => {
          if (checkoutAfterQuestionnaire) {
            setShowQuestionnaire(false);
            setPendingAttendance(null);
            setCheckoutAfterQuestionnaire(false);
          }
        }}
      />
      <Modal
        isOpen={openModal}
        title={t("traineeDetails.currentLocation")}
        onClose={() => {
          setOpenModal(false);
          setIsProcessing(false); // Reset processing state when modal closes
        }}
      >
        <CurrentLocation
          handleSave={recordTraineeAttendance}
          data={{
            latitude: selectedIndustry?.latitude,
            longitude: selectedIndustry?.longitude,
          }}
        />
      </Modal>

      <Modal
        isOpen={showSelfieModal}
        title="Take Selfie"
        onClose={closeSelfieModal}
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-gray-600">
            Allow camera access, then click Capture when your face is clearly
            visible.
          </p>
          {isCameraStarting && (
            <p className="text-sm text-primary">Starting camera...</p>
          )}
          {cameraError && (
            <p className="text-sm text-red-500">{cameraError}</p>
          )}
          <div className="w-full bg-black rounded overflow-hidden aspect-[3/4] max-h-[55vh] flex items-center justify-center">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
          </div>
          <canvas ref={canvasRef} className="hidden" />
          <div className="flex justify-center gap-2">
            <Button
              type="button"
              className="px-[10px] font-normal"
              title="Capture"
              onClick={captureSelfie}
              disabled={!!cameraError || isCameraStarting}
            />
            <button
              type="button"
              onClick={closeSelfieModal}
              className="py-2 px-6 rounded-md border border-gray-300 text-gray-700 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TraineeDetails;
